import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { UIThemeProvider } from './context/UIContext';
import config from './config';

import Login from './pages/Login';
import DashboardV2 from './pages-v2/DashboardV2';
import EarningsV2 from './pages-v2/EarningsV2';
import NetworkV2 from './pages-v2/NetworkV2';
import StatementsV2 from './pages-v2/StatementsV2';
import UsersV2 from './pages-v2/admin/UsersV2';
import UserDetailV2 from './pages-v2/admin/UserDetailV2';
import ChangePasswordV2 from './pages-v2/ChangePasswordV2';
import CustomersV2 from './pages-v2/CustomersV2';
import CustomerDetailV2 from './pages-v2/CustomerDetailV2';
import InviteCodesV2 from './pages-v2/InviteCodesV2';
import CustomersAdminV2 from './pages-v2/CustomersAdminV2';

function PrivateRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div>Loading...</div>;
  return user ? children : <Navigate to="/" />;
}

// Admin-only route - requires tier 0 (admin) or tier 4 (operations)
function AdminRoute({ children }) {
  const { user, loading, isAdmin, isOperations } = useAuth();
  if (loading) return <div>Loading...</div>;
  if (!user) return <Navigate to="/" />;
  if (!isAdmin && !isOperations) return <Navigate to="/dashboard" />;
  return children;
}

// No-Statements route - redirects operations (tier=4) away from Statements
function NoStatementsRoute({ children }) {
  const { user, loading, isOperations } = useAuth();
  if (loading) return <div>Loading...</div>;
  if (!user) return <Navigate to="/" />;
  if (isOperations) return <Navigate to="/dashboard" />;
  return children;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<Login />} />
      <Route path="/dashboard" element={<PrivateRoute><DashboardV2 /></PrivateRoute>} />
      <Route path="/earnings" element={<PrivateRoute><EarningsV2 /></PrivateRoute>} />
      <Route path="/network" element={<PrivateRoute><NetworkV2 /></PrivateRoute>} />
      <Route path="/statements" element={<PrivateRoute><NoStatementsRoute><StatementsV2 /></NoStatementsRoute></PrivateRoute>} />
      <Route path="/customers" element={<PrivateRoute><CustomersV2 /></PrivateRoute>} />
      <Route path="/customers/:mobile" element={<PrivateRoute><CustomerDetailV2 /></PrivateRoute>} />
      <Route path="/admin/users" element={<AdminRoute><UsersV2 /></AdminRoute>} />
      <Route path="/admin/users/:id" element={<AdminRoute><UserDetailV2 /></AdminRoute>} />
      <Route path="/admin/audit-logs" element={<AdminRoute><Navigate to="/dashboard" replace /></AdminRoute>} />
      <Route path="/admin/invite-codes" element={<AdminRoute><InviteCodesV2 /></AdminRoute>} />
      <Route path="/admin/customers" element={<AdminRoute><CustomersAdminV2 /></AdminRoute>} />
      <Route path="/admin/customers/:mobile" element={<AdminRoute><CustomerDetailV2 /></AdminRoute>} />
      <Route path="/change-password" element={<PrivateRoute><ChangePasswordV2 /></PrivateRoute>} />
      {/* v0.5: Risk module removed; keep hard redirect for legacy links/bookmarks */}
      <Route path="/admin/risk" element={<PrivateRoute><Navigate to="/dashboard" replace /></PrivateRoute>} />
      <Route path="/risk" element={<PrivateRoute><Navigate to="/dashboard" replace /></PrivateRoute>} />
    </Routes>
  );
}

function App() {
  return (
    <UIThemeProvider>
      <AuthProvider>
        <BrowserRouter basename={config.publicUrl}>
          <AppRoutes />
        </BrowserRouter>
      </AuthProvider>
    </UIThemeProvider>
  );
}

export default App;
